import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import { Value } from 'baseui/select';
import { AppContext, UserContext, parseString } from 'commons-ui';

import { GAME_ADMIN_RESOURCES, GAME_ADMIN_SCOPES, PAGE_NAMES } from 'constant';
import usePermission from 'routes/usePermission';

import { GAME_KEY_MAP, DATA_TYPES } from '../../utils/helper';
import useValuesPicker from '../../utils/useValuesPicker';
import useService from '../../utils/useService';

const useEdit = () => {
	const [keyValue, setKeyValue] = useState('');
	const [description, setDescription] = useState('');
	const [newKeyValue, setNewKeyValue] = useState('');
	const [pickerValue, setPickerValue] = useState<Value>([]);
	const [oldValueType, setOldValueType] = useState('');
	const [confirmationModal, setConfirmationModal] = useState<{
		show: boolean;
		message?: string;
		header?: string;
		handleAlertConfirmation?: () => void;
		handleAlertCancel?: () => void;
	}>({
		show: false
	});
	const isUpdateInitiated = useRef(false);

	const { getKeyValue, updateKeyValue } = useService();

	const { user } = useContext(UserContext);
	const { hasPermission } = usePermission();
	const { showAlert, app } = useContext(AppContext);
	const location = useLocation();
	const history = useHistory();
	const hasEditPermission = hasPermission({
		entity: GAME_ADMIN_RESOURCES.zookeeper,
		action: GAME_ADMIN_SCOPES[GAME_ADMIN_RESOURCES.zookeeper]?.updateKey,
		appName: app.appName,
		configPageName: PAGE_NAMES.gameAdmin
	});
	const { appName } = app;
	const { PICKER_VALUES, getTypeForValue, getActualValue, getPickerValueFromValueType } = useValuesPicker();

	const userName = _get(user, 'userName', '');

	const { keyName = '', serviceName = '' } = _get(location, 'state.keysData', {});

	useEffect(() => {
		const handleGetKey = async () => {
			const game = GAME_KEY_MAP[appName];
			const res = await getKeyValue({ keyName, serviceName, game });
			if (res?.success) {
				const valueOfKey = res.value;

				const valueType = getTypeForValue(valueOfKey);
				setPickerValue(getPickerValueFromValueType(valueType));
				setOldValueType(valueType);

				switch (valueType) {
					case DATA_TYPES.ARRAY:
					case DATA_TYPES.JSON:
						setKeyValue(JSON.stringify(valueOfKey));
						setNewKeyValue(JSON.stringify(valueOfKey));
						break;
					case DATA_TYPES.NULL:
						setKeyValue('null');
						setNewKeyValue('null');
						break;
					case DATA_TYPES.STRING:
						setKeyValue(parseString(valueOfKey));
						setNewKeyValue(parseString(valueOfKey));
						break;
					default:
						setKeyValue(valueOfKey);
						setNewKeyValue(valueOfKey);
				}
			}
		};
		handleGetKey();
	}, []);

	const handleKeyUpdate = () => {
		if (pickerValue.length === 0) {
			showAlert('Please Select Key Type', { type: 'error' });
			return;
		}
		if (description === '') {
			showAlert('You need to provide Description', { type: 'error' });
			return;
		}
		const { error, actualKeyValue } = getActualValue(pickerValue[0].keyType, newKeyValue);
		if (error) {
			return;
		}

		setConfirmationModal({
			show: true,
			header: `Changing value of ${keyName}`,
			message: `Do you wish to change value of ${keyName},{type: ${oldValueType}} from ${keyValue} to ${newKeyValue},{type: ${pickerValue[0].keyType}}`,
			handleAlertCancel: () => {
				if (isUpdateInitiated.current) {
					return;
				}
				showAlert('Failed to change value', { type: 'error' });
				setConfirmationModal({ show: false });
			},
			handleAlertConfirmation: async () => {
				isUpdateInitiated.current = true;
				const res = await updateKeyValue({
					keyName,
					serviceName,
					agent: userName,
					desc: description,
					data: actualKeyValue,
					game: GAME_KEY_MAP[appName]
				});
				if (res?.success) {
					showAlert('Key Update was Submitted for Review', {
						type: 'success',
						duration: 'INFINITE',
						actionMessage: 'OK',
						actionOnClick: () => {
							history.goBack();
						}
					});
				} else {
					showAlert(`Key Update Submission For Review failed. ${res.message}`, {
						type: 'error',
						actionMessage: 'OK',
						actionOnClick: () => {
							history.goBack();
						}
					});
				}
			}
		});
	};

	const handleKeyEdit = (data: string) => {
		setNewKeyValue(data);
	};

	const handleEnterDescription = (data: string) => {
		setDescription(data);
	};

	const handleResetChanges = () => {
		setNewKeyValue(keyValue);
	};

	const handleKeyCancel = () => {
		history.goBack();
	};
	return {
		PICKER_VALUES,
		pickerValue,
		newKeyValue,
		keyName,
		keyValue,
		description,
		confirmationModal,
		hasEditPermission,
		setConfirmationModal,
		setPickerValue,
		handleKeyUpdate,
		handleResetChanges,
		handleKeyEdit,
		handleEnterDescription,
		handleKeyCancel
	};
};

export default useEdit;
